import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';
import { type ToastMessage, type ToastType, useToast } from './useToast';

const AUTO_CLOSE_MS = 8000;

/**
 * Light theme keeps the glassy translucent look (tint + backdrop blur over a white page). Dark
 * overrides switch to the opaque semantic status surfaces: a 10%-tinted background over a dark page
 * lets whatever is behind (e.g. the decorative page-corner artwork) bleed through the blur and
 * wreck the text contrast.
 */
const typeConfig: Record<
  ToastType,
  { icon: React.ElementType; bg: string; border: string; text: string; iconColor: string }
> = {
  info: {
    icon: InformationCircleIcon,
    bg: 'bg-info-500/10 dark:bg-status-info-bg',
    border: 'border-info-500/30 dark:border-status-info-border',
    text: 'text-info-500 dark:text-status-info-fg',
    iconColor: 'text-info-400 dark:text-status-info-icon',
  },
  success: {
    icon: CheckCircleIcon,
    bg: 'bg-success-500/10 dark:bg-status-success-bg',
    border: 'border-success-500/30 dark:border-status-success-border',
    text: 'text-success-500 dark:text-status-success-fg',
    iconColor: 'text-success-400 dark:text-status-success-icon',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bg: 'bg-primary-500/10 dark:bg-status-warning-bg',
    border: 'border-primary-500/30 dark:border-status-warning-border',
    text: 'text-primary-700 dark:text-status-warning-fg',
    iconColor: 'text-primary-600 dark:text-status-warning-icon',
  },
  error: {
    icon: ExclamationCircleIcon,
    bg: 'bg-danger-500/10 dark:bg-status-danger-bg',
    border: 'border-danger-500/30 dark:border-status-danger-border',
    text: 'text-danger-400 dark:text-status-danger-fg',
    iconColor: 'text-danger-400 dark:text-status-danger-icon',
  },
};

const ToastItem = ({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) => {
  const config = typeConfig[toast.type];
  const Icon = config.icon;
  const isUrgent = toast.type === 'error';

  // `toast.count` is a dependency so that each merged duplicate restarts the auto-dismiss timer.
  React.useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.timeout ?? AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [toast.id, toast.timeout, toast.count, onDismiss]);

  return (
    <motion.div
      layout
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={cn(
        // ↓ `text-sm` sits on the row (not only on the message) so the icon's `1lh` below
        //   resolves against the message's line box.
        'relative flex items-start gap-3 rounded-lg border p-3 text-sm shadow-lg pointer-events-auto w-[400px] max-w-[calc(100vw-2rem)] backdrop-blur-md bg-opacity-80',
        config.bg,
        config.border
      )}
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      {toast.count && toast.count > 1 ? (
        <span
          className={cn(
            'pointer-events-none absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 rounded-full border bg-surface-raised px-1.5 text-xs font-semibold leading-5 tabular-nums',
            config.border,
            config.text
          )}
          aria-label={`${toast.count} occurrences`}
        >
          {toast.count}
        </span>
      ) : null}
      <Icon className={cn('align-first-line-5', config.iconColor)} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className={cn('text-sm break-words', config.text)}>
          <span className="break-words">{toast.message}</span>
        </div>
        {toast.actions && toast.actions.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            {toast.actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => {
                  action.onClick();
                  onDismiss(toast.id);
                }}
                className={cn(
                  'text-xs font-medium px-2 py-1 rounded-md transition-colors duration-200 cursor-pointer border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  'bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15',
                  config.text
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded-md p-1 text-text-muted hover:text-text-primary hover:bg-black/5 dark:hover:bg-white/10 transition-colors duration-200 cursor-pointer bg-transparent border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/**
 * True when the event originated inside the toast region. Modal layers (Sheet/Dialog) use this
 * to keep toast interactions (e.g. the dismiss button) from counting as "outside" dismissals.
 */
export const isEventInsideToaster = (event: { target: EventTarget | null }): boolean =>
  Boolean((event.target as HTMLElement | null)?.closest?.('[data-toaster]'));

export const Toaster = () => {
  const { toasts, removeToast } = useToast();

  // Portaled to <body>: rendered inline, any transformed/filtered ancestor would trap the
  // z-index in its own stacking context, letting body-level portals (Sheet/Dialog overlays)
  // paint on top of the toasts.
  return createPortal(
    <section
      data-toaster
      className="fixed top-[76px] right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      aria-label="Toast notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </AnimatePresence>
    </section>,
    document.body
  );
};
